/**
 * GoogleMyBusinessContent zeigt eine Lister aller Posts für eine bestimmte PageId an
 * Inhalte werden direkt hier geladen
 * Token und Basepath wird dazu aus dem Storage geholt
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ScreenEnum, ViewEnum } from '@professocial/professocialTypes/types/commonTypes'
import DropDownComponent, {
  DropDownElement,
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import { Box, Grid, Skeleton, Theme } from '@mui/material'
import Pagination from '@professocial/professocialFormComponents/FormComponents/Pagination'
import { makeStyles } from '@mui/styles'
import {
  ActionType,
  DeferredPost,
  DeferredPostSource,
  DeferredPostStatus,
  GetGooglePageFeedRequest,
  GooglePost,
  NetworkType,
} from '@3m5/sm_publish-frontend-api'

import ImportProfessocialPost from './ImportProfessocialPost'
import useGalleryWidth from '../../util/useGalleryWidth'
import { getTheme, getUser } from '../../store/common/common.selectors'
import getAppTheme from '../../defaults/getAppTheme'
import { getGooglePageFeeds } from '../../store/googleMyBusiness/googleMyBusiness.selector'
import { getGooglePageFeedInitiated } from '../../store/googleMyBusiness/googleMyBusiness.actions'
import CheckBoxComponent from '../../shared/components/CheckBoxComonent'

export interface Props {
  networkPageId: string
  networkUserId: string
}

const useStyles = makeStyles((theme: Theme) => ({
  uList: {
    display: 'flex',
    flexWrap: 'wrap',
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    width: '100%',
  },
}))

const GoogleMyBusinessContent: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles()

  const theme = useSelector(getTheme)
  const user = useSelector(getUser)

  const [page, setPage] = useState(1)
  const [data, setData] = useState<GooglePost[]>([])
  const [dataPerPage, setDataPerPage] = useState<GooglePost[]>([])

  const storedGoogleMyBusinessData: GooglePost[] | undefined = useSelector(getGooglePageFeeds)
  const dispatchGetGooglePageFeeds = useCallback((params: GetGooglePageFeedRequest) => dispatch(getGooglePageFeedInitiated(params)), [dispatch])

  const mediaContainerRef = useRef<HTMLDivElement>(null)
  const { itemsPerPage, elementSize } = useGalleryWidth(mediaContainerRef, null, 700, 4)

  // TODO: löschen, wenn die Daten ankommen
  const mockPostingForGoogleMyBusinessImage: GooglePost =
    {
      id: '583747483025679976',
      message: 'Das ist ein langer Text mit viel Inhalt und mehreren Zeilen, damit wir auch das mal testen können.',
      createdAt: new Date('2022-10-04'),
      ctaButton: { actionType: ActionType.Call, url: 'www.google.de' },
      mediaUrls: ['https://www.butenunbinnen.de/bilder/testbild-sw-100~_v-512x288_c-1594546669515.jpg', 'https://upload.wikimedia.org/wikipedia/commons/9/95/SW_Testbild_auf_Philips_TD1410U.jpg', 'https://cdn.pixabay.com/photo/2013/07/12/17/47/test-pattern-152459__480.png'],
    }
  const mockPostingForGoogleMyBusiness: DeferredPost =
    {
      id: '583747483025679976',
      networkType: NetworkType.GoogleMyBusiness,
      networkPageId: '7938844042347049862',
      source: DeferredPostSource.AutomaticAbo,
      status: DeferredPostStatus.TokenInvalid,
      overridingPostContent: {
        text: 'Lorem ipsum und noch mehr Gedöns',
        image: {
          url: 'https://cdn.pixabay.com/photo/2013/07/12/17/47/test-pattern-152459__480.png',
          mediaId: '0815',
          description: 'Das ist en Hilfstext',
        },
        extensions: {
          googleMyBusinessExtension: { ctaActionType: ActionType.Call, ctaLink: 'www.google.de' },
        },
      },
      postStart: new Date('2022-10-06'),
    }

  useEffect(() => {
    const params: GetGooglePageFeedRequest = {
      networkPageId: props.networkPageId,
      networkUserId: props.networkUserId,
    }
    dispatchGetGooglePageFeeds(params)
  }, [props.networkPageId])

  useEffect(() => {
    storedGoogleMyBusinessData
      ? setData(storedGoogleMyBusinessData)
      : setData([mockPostingForGoogleMyBusinessImage, mockPostingForGoogleMyBusinessImage, mockPostingForGoogleMyBusiness, mockPostingForGoogleMyBusiness, mockPostingForGoogleMyBusiness])
  }, [storedGoogleMyBusinessData])

  const handlePageChange = (page = 1) => {
    if (data) {
      const startItem = (page - 1) * itemsPerPage
      const endItem = page * itemsPerPage
      setDataPerPage(data.slice(startItem, endItem))
    }
  }

  useEffect(() => {
    handlePageChange()
  }, [data, itemsPerPage])

  useEffect(() => {
    handlePageChange(page)
  }, [page])

  /**
   * show only manual posts
   */
  const showMaunualPosts = (checked: boolean) => {
    if (checked) {
      setData(data.filter((dataEntry) => dataEntry.id == null))
    }
  }

  /**
   * show only published or unpublished posts
  */
  const handleChange = (selected: DropDownElement) => {
    const current = new Date()
    const tempFeeds: GooglePost[] = []
    if (selected.key === 'unpublished') {
      data.map((dataEntry) => { if (dataEntry.createdAt && dataEntry.createdAt > current) tempFeeds.push(dataEntry) })
    } else if (selected.key === 'published') {
      data.map((dataEntry) => { if (dataEntry.createdAt && dataEntry.createdAt <= current) tempFeeds.push(dataEntry) })
    } else {
      storedGoogleMyBusinessData && setData(storedGoogleMyBusinessData)
    }

    setData(tempFeeds)
  }

  return (
    <Grid container ref={mediaContainerRef}>
      <Grid container item xs={12} sx={{ justifyContent: 'end' }}>
        <Grid item xs={2}>
          <CheckBoxComponent onChange={(checked: boolean) => showMaunualPosts(checked)} label={t('googleMyBusinessContent:showMaunualPosts')} />
        </Grid>
        <Grid item xs={3} sx={{ marginRight: getAppTheme().spacing(3) }}>
          <DropDownComponent
            variant='standard'
            disableClearable
            options={[
              {
                key: 'all',
                value: '',
              },
              {
                key: 'unpublished',
                value: t('googleMyBusinessContent:showUnpublishedPosts'),
              },
              {
                key: 'published',
                value: t('googleMyBusinessContent:showPublishedPosts'),
              },
            ]}
            label={t('googleMyBusinessContent:showPosts')}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
      <Grid xs={12} item container>
        <ul className={classes.uList}>
          {data && dataPerPage ? dataPerPage.map((item: any, index: number) => {
            return (
              <Grid item xs={12} md={6} key={index}>
                <li key={index} style={{ margin: '20px' }}>
                  <ImportProfessocialPost
                    postConfiguration={{
                      theme: theme,
                      userRole: user?.userRole,
                      size: ScreenEnum.xl,
                      view: ViewEnum.overview,
                      networkPageId: props.networkPageId,
                      networkType: NetworkType.GoogleMyBusiness,
                      googleMyBusinessPost: item,
                      elementSize: elementSize - 50,
                    }}
                  />
                </li>
              </Grid>
            )
          }) : (
            <>
              {[...Array(itemsPerPage)].map((x, i) => (
                <li key={i}><Skeleton style={{ margin: '15px' }} variant='rectangular' width={elementSize} height={150} /></li>
              ),
              )}
              <Box style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                <Skeleton style={{ marginTop: '24px' }} variant='rectangular' width={305} height={25} />
              </Box>
            </>
          )}
        </ul>
      </Grid>
      <Grid xs={12} item>
        <Box component='div' style={{ display: 'flex', justifyContent: 'center' }}>
          {data && (
            <Pagination
              totalItems={data.length}
              itemsPerPage={itemsPerPage}
              page={page}
              setPage={setPage}
            />
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default GoogleMyBusinessContent
